import type { TodayCardActionData, TodayCardOutcome } from '../utils/todayCardTelemetry';
import type {
    IMyOutlookBriefingAnnotation,
    IMyOutlookCreditsAnnotation,
    IMyOutlookCreditsCostAxis,
    IMyOutlookCreditsPolicyCostAxis,
    IMyOutlookEligibilityAnnotation,
    IMyOutlookLoadingPreviewAnnotation,
    IMyOutlookState,
    MyOutlookTaskStatus,
    MyOutlookCreditsCostReadOutcome,
    MyOutlookCreditsEvaluationResult as AugLoopCreditsEvaluationResult,
    MyOutlookBriefingReadOutcome,
    IMyOutlookUserActionSupport,
    MyOutlookEventResponse,
} from '@augloop-types/my-outlook';
import type {
    MyOutlookCommandKind,
    MyOutlookLiveItemResult,
    MyOutlookLiveMessage,
    MyOutlookSimulationScenario,
    MyOutlookUserActionRequest,
    MyOutlookUserActionResult,
    MyOutlookReplyRequest,
    MyOutlookReplyActionResult,
} from 'owa-personal-os-service/lib/MyOutlookClient';
import type {
    BriefingSchedule,
    BriefingScheduleReconciliationState,
    BriefingScheduleSource,
} from 'owa-personal-os-types';
import { createStore } from 'owa-satcheljs';
import {
    MY_OUTLOOK_BRIEFING_UPDATE_CURRENT,
    type MyOutlookBriefingUpdate,
} from './myOutlookBriefingUpdate';

export type MyOutlookAvailability =
    | 'unknown'
    | 'available'
    | 'unavailable'
    | 'denied'
    | 'notEligible';

export type MyOutlookReadStatus = 'notLoaded' | 'loading' | 'loaded';
export type MyOutlookPublishedBriefing = NonNullable<
    IMyOutlookBriefingAnnotation['publication']
> & {
    /** The Typed read Today renders: `content`, `citationsJson`, and `offersPin`, as JSON. */
    readonly briefingJson?: string;
};
export interface MyOutlookPinnedBriefing {
    /** The pinned cards Today renders, as JSON. */
    readonly pinnedBriefingJson: string;
}
export type MyOutlookBriefingAvailability =
    | 'unknown'
    | 'ready'
    | 'noBriefing'
    | 'unavailable'
    | 'denied'
    | 'notEligible'
    | 'unsupportedRepresentation'
    | 'projectionFailed';

export type { MyOutlookCommandKind };

export type MyOutlookCommandStatus =
    | 'submitting'
    | 'accepted'
    | 'completed'
    | 'alreadySatisfied'
    | 'failed'
    | 'rejected'
    | 'unavailable'
    | 'unconfirmed';

export interface MyOutlookCommandState {
    readonly commandId?: string;
    /** An earlier attempt's ID kept through a local rejection while its outcome is unknown. */
    readonly retryCommandId?: string;
    /** Admission was acknowledged, so same-ID reconciliation cannot create new work. */
    readonly wasAccepted?: boolean;
    readonly status: MyOutlookCommandStatus;
    readonly operationId?: string;
    readonly isFinalResponse?: boolean;
    readonly errorCode?: string;
}

export interface MyOutlookProductState {
    readonly allowedCommands: readonly string[];
    readonly userActionSupport?: IMyOutlookUserActionSupport;
    readonly readStatus: MyOutlookReadStatus;
    readonly availability: MyOutlookAvailability;
    readonly state: IMyOutlookState | null;
    readonly reasonCode?: string;
    readonly commands: Partial<Record<MyOutlookCommandKind, MyOutlookCommandState>>;
    readonly briefingReadStatus: MyOutlookReadStatus;
    readonly briefingAvailability: MyOutlookBriefingAvailability;
    readonly briefingReasonCode?: string;
    readonly displayedBriefing: MyOutlookPublishedBriefing | null;
    readonly seenBriefing?: Pick<MyOutlookPublishedBriefing, 'id' | 'generatedAt'>;
    /** A cache-hydrated startup gets one automatic live reconciliation, not a generation request. */
    readonly briefingHydration?: 'checking' | 'loading' | 'failed';
    readonly pinnedBriefing: MyOutlookPinnedBriefing | null;
    readonly briefingUpdate: MyOutlookBriefingUpdate;
    readonly briefingReadResponse?: IMyOutlookBriefingAnnotation;
    /** Last confirmed read for the displayed publication, independent of an in-flight read. */
    readonly displayedBriefingReadResponse?: IMyOutlookBriefingAnnotation;
    readonly retainedBriefing?: {
        readonly response: IMyOutlookBriefingAnnotation;
        readonly readAt: number;
    };
    readonly retainedBriefingReadFailed?: boolean;
    readonly retainedBriefingReadOutcome?: MyOutlookBriefingReadOutcome;
    readonly briefingReadRetryAt?: number;
    /** Ephemeral Today-visit observations, independent of product and generation state. */
    readonly loadingPreview?: IMyOutlookLoadingPreviewAnnotation;
}

export type MyOutlookCreditsAvailability = 'unknown' | 'ready' | 'unavailable' | 'denied';
export type MyOutlookCreditsEvaluationResult = `${AugLoopCreditsEvaluationResult}`;

export interface MyOutlookCreditsState {
    readonly readStatus: MyOutlookReadStatus;
    readonly availability: MyOutlookCreditsAvailability;
    readonly remainingCredits?: number;
    readonly monthlyQuota?: number;
    readonly resetOn?: string;
    readonly lastSuccessfulReadAt?: string;
    readonly hasConsumptionBillingEnabled?: boolean;
    readonly hasCreditsAvailable?: boolean;
    readonly hasM365CopilotLicense?: boolean;
    readonly uslEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly capacityEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly billingPolicyEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly costReadOutcome?: MyOutlookCreditsCostReadOutcome;
    readonly userCost?: IMyOutlookCreditsCostAxis;
    readonly policyCost?: IMyOutlookCreditsPolicyCostAxis;
    readonly costAsOfDate?: string;
    readonly costCycleStart?: string;
    readonly costResetOn?: string;
    readonly lastSuccessfulCostReadAt?: string;
    readonly costReasonCode?: string;
    readonly reasonCode?: string;
    readonly readResponse?: IMyOutlookCreditsAnnotation;
}

export type MyOutlookEligibilityAvailability = 'unknown' | 'ready' | 'unavailable' | 'denied';

export interface MyOutlookEligibilityState {
    readonly readStatus: MyOutlookReadStatus;
    readonly availability: MyOutlookEligibilityAvailability;
    readonly isFeatureVisible?: boolean;
    readonly hasM365CopilotLicense?: boolean;
    readonly hasConsumptionBillingEnabled?: boolean;
    readonly hasCreditsAvailable?: boolean;
    readonly uslEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly billingPolicyEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly capacityEvaluationResult?: MyOutlookCreditsEvaluationResult;
    readonly reasonCode?: string;
    readonly readResponse?: IMyOutlookEligibilityAnnotation;
}

export type MyOutlookBriefingScheduleAvailability = 'unknown' | 'ready' | 'unavailable';
export type MyOutlookBriefingScheduleSaveStatus = 'idle' | 'saving' | 'failed' | 'conflict';

export interface MyOutlookBriefingScheduleState {
    readonly readStatus: MyOutlookReadStatus | 'failed';
    readonly readReasonCode?: string;
    readonly readGeneration?: number;
    readonly availability: MyOutlookBriefingScheduleAvailability;
    readonly source?: BriefingScheduleSource;
    readonly schedule: BriefingSchedule | null;
    readonly draft: BriefingSchedule | null;
    readonly revision?: number;
    readonly reconciliationState?: BriefingScheduleReconciliationState;
    readonly reasonCode?: string;
    readonly saveStatus: MyOutlookBriefingScheduleSaveStatus;
}

export interface MyOutlookContextState {
    readonly ownerKey: string;
    readonly product: MyOutlookProductState;
    readonly briefingActions: BriefingActionState;
    readonly credits: MyOutlookCreditsState;
    readonly eligibility: MyOutlookEligibilityState;
    readonly briefingSchedule: MyOutlookBriefingScheduleState;
    readonly briefingLive: BriefingLiveState;
}

/**
 * LiveStateRead facts for the displayed publication: a read-only overlay that never replaces
 * the publication, dismisses a card, or settles an operation.
 */
export interface BriefingLiveState {
    readonly publicationId?: string;
    /** By card reference. A later partial read keeps facts it could not read again. */
    readonly items: Readonly<Record<string, MyOutlookLiveItemResult>>;
    /** By served email ID, across every card that serves it. */
    readonly messages: Readonly<Record<string, MyOutlookLiveMessage>>;
}

export const EMPTY_BRIEFING_LIVE_STATE: BriefingLiveState = { items: {}, messages: {} };

export type BriefingMailVerb = 'archiveMessage' | 'deleteMail' | 'markMailRead';
export type BriefingRsvpChoice = `${MyOutlookEventResponse}`;

/**
 * `unknown` means a write was dispatched without a definitive result. It may have
 * succeeded, so it is never resent automatically or by an explicit retry. `failed` and
 * `unsent` (reserved but never dispatched) may be retried explicitly.
 */
export type BriefingMessageStatus = 'pending' | 'succeeded' | 'failed' | 'unknown' | 'unsent';

export interface BriefingMessageOperation {
    readonly verb: BriefingMailVerb;
    readonly messageId: string;
    readonly status: BriefingMessageStatus;
    /** Moves change EWS IDs, so a resolved ID is retained with its outcome. */
    readonly itemId?: string;
}

export interface BriefingResponseOperation {
    readonly status: 'pending' | 'confirmed' | 'failed' | 'unconfirmed';
    readonly response: BriefingRsvpChoice;
    /** The earlier confirmed response that stands while a change is unsettled or failed. */
    readonly confirmedResponse?: BriefingRsvpChoice;
    /** The briefing action the response was sent from; only it announces the outcome. */
    readonly origin?: string;
}

/**
 * One UserAction slot per item and action kind; Pin and Unpin share a slot. `unconfirmed`
 * may have been applied, so only the same request may be sent until it is reconciled.
 */
export interface BriefingUserActionOperation {
    /** Immutable presentation correlation; never part of the service request. */
    readonly telemetry?: TodayCardActionData;
    readonly desiredTelemetry?: TodayCardActionData;
    readonly telemetryAttemptLogged?: boolean;
    readonly telemetryOutcomes?: readonly TodayCardOutcome[];
    /** Emits once before context teardown; never cancels or settles the service write. */
    readonly cancelTelemetry?: () => void;
    readonly confirmedRatingValue?: -1 | 0 | 1;
    readonly request: MyOutlookUserActionRequest;
    readonly status:
        | 'pending'
        | 'confirmed'
        | 'unconfirmed'
        | 'failed'
        | 'conflict'
        | 'rejected'
        | 'notEnabled';
    readonly result?: MyOutlookUserActionResult;
    /** Last confirmed task status while a later edit is pending or failed. */
    readonly confirmedTaskStatus?: MyOutlookTaskStatus;
    /** Service-owned Undo of this action. */
    readonly undo?: BriefingUserActionOperation;
    /**
     * Owner choices (pin, rating, dismissal): the latest value the user chose, and the value
     * the service last acknowledged. Presentation uses them until a read reconciles them.
     */
    readonly desired?: number;
    readonly acknowledged?: number;
    /** The service confirmed `acknowledged`; otherwise it is the state the first choice saw. */
    readonly choiceConfirmed?: true;
    /** A dismissal Undo was refused locally because the service granted no open window. */
    readonly undoExpired?: boolean;
}

/**
 * Operation facts owned by the account context, keyed by service target rather than by
 * presentation, so card replacement, remounts, and duplicate cards share one write.
 */
export interface BriefingActionState {
    readonly messages: Readonly<Record<string, BriefingMessageOperation>>;
    readonly batches: Readonly<Record<string, readonly string[]>>;
    readonly responses: Readonly<Record<string, BriefingResponseOperation>>;
    readonly actions: Readonly<Record<string, BriefingUserActionOperation>>;
    readonly replies?: Readonly<Record<string, BriefingReplyOperation | undefined>>;
    /** The owner's edits to a reply's generated text, keyed like `replies`; absent until edited. */
    readonly replyDrafts?: Readonly<
        Record<string, { readonly publicationId: string; readonly body: string } | undefined>
    >;
}

export interface BriefingReplyOperation {
    readonly request: MyOutlookReplyRequest;
    readonly status:
        | 'preparing'
        | 'prepared'
        | 'sending'
        | 'submitted'
        | 'unconfirmed'
        | 'failed'
        | 'expired';
    readonly result?: MyOutlookReplyActionResult;
    /** Send stopped because the reply would reach people other than those shown. */
    readonly recipientsChanged?: boolean;
}

export const EMPTY_BRIEFING_ACTION_STATE: BriefingActionState = {
    messages: {},
    batches: {},
    responses: {},
    actions: {},
};

export interface MyOutlookProductStore {
    contextsByAccount: Record<string, MyOutlookContextState>;
    simulationsByAccount: Record<string, { scenario: MyOutlookSimulationScenario; step: number }>;
    contextEpoch: number;
    accountEpochs: Record<string, number>;
}

export const EMPTY_PRODUCT_STATE: MyOutlookProductState = {
    allowedCommands: [],
    readStatus: 'notLoaded',
    availability: 'unknown',
    state: null,
    commands: {},
    briefingReadStatus: 'notLoaded',
    briefingAvailability: 'unknown',
    displayedBriefing: null,
    pinnedBriefing: null,
    briefingUpdate: MY_OUTLOOK_BRIEFING_UPDATE_CURRENT,
};

export const EMPTY_CREDITS_STATE: MyOutlookCreditsState = {
    readStatus: 'notLoaded',
    availability: 'unknown',
};

export const EMPTY_ELIGIBILITY_STATE: MyOutlookEligibilityState = {
    readStatus: 'notLoaded',
    availability: 'unknown',
};

export const EMPTY_BRIEFING_SCHEDULE_STATE: MyOutlookBriefingScheduleState = {
    readStatus: 'notLoaded',
    availability: 'unknown',
    schedule: null,
    draft: null,
    saveStatus: 'idle',
};

export function createDefaultMyOutlookContextState(ownerKey: string): MyOutlookContextState {
    return {
        ownerKey,
        product: EMPTY_PRODUCT_STATE,
        briefingActions: EMPTY_BRIEFING_ACTION_STATE,
        credits: EMPTY_CREDITS_STATE,
        eligibility: EMPTY_ELIGIBILITY_STATE,
        briefingSchedule: EMPTY_BRIEFING_SCHEDULE_STATE,
        briefingLive: EMPTY_BRIEFING_LIVE_STATE,
    };
}

const getMyOutlookProductStore: () => MyOutlookProductStore = createStore<MyOutlookProductStore>(
    'MyOutlookProductStore',
    {
        contextsByAccount: {},
        simulationsByAccount: {},
        contextEpoch: 0,
        accountEpochs: {},
    }
);

function cancelPendingBriefingTelemetry(context: MyOutlookContextState): void {
    for (const operation of Object.values(context.briefingActions.actions)) {
        if (operation.status === 'pending') {
            operation.cancelTelemetry?.();
        }
        if (operation.undo?.status === 'pending') {
            operation.undo.cancelTelemetry?.();
        }
    }
}

export function clearMyOutlookProductStateForAccountFields(accountKey: string): void {
    const store = getMyOutlookProductStore();
    const context = store.contextsByAccount[accountKey];
    if (context) {
        cancelPendingBriefingTelemetry(context);
    }
    store.accountEpochs = {
        ...store.accountEpochs,
        [accountKey]: (store.accountEpochs[accountKey] ?? 0) + 1,
    };
    if (!(accountKey in store.contextsByAccount)) {
        return;
    }
    const { [accountKey]: _removedContext, ...remainingContexts } = store.contextsByAccount;
    store.contextsByAccount = remainingContexts;
}

export function resetMyOutlookProductStateFields(): void {
    const store = getMyOutlookProductStore();
    for (const context of Object.values(store.contextsByAccount)) {
        cancelPendingBriefingTelemetry(context);
    }
    store.contextEpoch++;
    store.accountEpochs = {};
    store.contextsByAccount = {};
    store.simulationsByAccount = {};
}

export default getMyOutlookProductStore;
